// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import Guilds from '@app/features/guild/state/Guilds';
import * as GuildTagCommands from '@app/features/guild_tag/commands/GuildTagCommands';
import {WEAR_FAILED_DESCRIPTOR} from '@app/features/guild_tag/hooks/useGuildTagToggle';
import GuildTags from '@app/features/guild_tag/state/GuildTags';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Combobox} from '@app/features/ui/components/form/FormCombobox';
import Users from '@app/features/user/state/Users';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useMemo, useState} from 'react';

const SECTION_TITLE_DESCRIPTOR = msg({
	message: 'Server tag',
	comment: 'Section title in profile settings for choosing which community tag to show beside your name.',
});
const SECTION_DESCRIPTION_DESCRIPTOR = msg({
	message:
		'Show the tag of a community you are in beside your name. Everyone who sees your name also sees the tag, in every community.',
	comment: 'Description of the server tag section in profile settings.',
});
const NO_TAG_DESCRIPTOR = msg({
	message: 'No tag',
	comment: 'Choice in the server tag picker in profile settings that shows no tag.',
});

const NONE = '';

export const GuildTagWearSection: React.FC = observer(() => {
	const {i18n} = useLingui();
	const [isSaving, setIsSaving] = useState(false);
	const worn = Users.currentUser?.tagGuildId ?? null;
	const guilds = Guilds.getGuilds();
	const options = useMemo(
		() => [
			{value: NONE, label: i18n._(NO_TAG_DESCRIPTOR)},
			...guilds
				.filter((guild) => guild.guildTag)
				.map((guild) => ({value: guild.id, label: `${guild.guildTag} · ${guild.name}`})),
		],
		[guilds, i18n.locale],
	);
	const handleChange = useCallback(async (value: string) => {
		setIsSaving(true);
		try {
			await GuildTagCommands.wear(value === NONE ? null : value);
		} catch {
			ToastCommands.createToast({type: 'error', children: i18n._(WEAR_FAILED_DESCRIPTOR)});
		} finally {
			setIsSaving(false);
		}
	}, []);
	if (!GuildTags.enabled || (options.length === 1 && worn === null)) return null;
	return (
		<SettingsSection
			id="server-tag"
			title={i18n._(SECTION_TITLE_DESCRIPTOR)}
			description={i18n._(SECTION_DESCRIPTION_DESCRIPTOR)}
			data-flx="guild-tag.guild-tag-wear-section.settings-section"
		>
			<Combobox
				value={worn ?? NONE}
				options={options}
				onChange={handleChange}
				disabled={isSaving}
				aria-label={i18n._(SECTION_TITLE_DESCRIPTOR)}
				data-flx="guild-tag.guild-tag-wear-section.combobox"
			/>
		</SettingsSection>
	);
});
